import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from './AuthContext';
import { buildAccountsFromSimplified, getActiveAccounts } from '../lib/simplifiedAccounts';
import { secureLog } from '../utils/securityUtils';
import type { TradingAccount } from '../types';
import type { SimplifiedAccountEntry, UserSimplifiedStats } from '../types/simplifiedStats';

interface SimplifiedStatsContextValue {
  simplifiedStats: UserSimplifiedStats | null;
  loading: boolean;
  activeAccounts: TradingAccount[];
  allAccounts: TradingAccount[];
  getAccountEntry: (accountId: string) => SimplifiedAccountEntry | null;
}

const SimplifiedStatsContext = createContext<SimplifiedStatsContextValue | undefined>(undefined);

/**
 * Une seule écoute Firestore sur users/{uid}/stats/simplified.
 * Liste des comptes + stats agrégées — pas de query accounts côté client.
 */
export function SimplifiedStatsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [simplifiedStats, setSimplifiedStats] = useState<UserSimplifiedStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.uid) {
      setSimplifiedStats(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    const statsRef = doc(db, 'users', user.uid, 'stats', 'simplified');

    const unsubscribe = onSnapshot(
      statsRef,
      (snapshot) => {
        setSimplifiedStats(snapshot.exists() ? (snapshot.data() as UserSimplifiedStats) : null);
        setLoading(false);
      },
      (error) => {
        secureLog.error('Erreur écoute stats/simplified:', error);
        setSimplifiedStats(null);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [user?.uid]);

  const allAccounts = useMemo(
    () => buildAccountsFromSimplified(user?.uid, simplifiedStats),
    [user?.uid, simplifiedStats],
  );

  const activeAccounts = useMemo(
    () => getActiveAccounts(allAccounts),
    [allAccounts],
  );

  const getAccountEntry = useCallback(
    (accountId: string) => simplifiedStats?.accounts?.[accountId] ?? null,
    [simplifiedStats],
  );

  const value = useMemo(
    () => ({
      simplifiedStats,
      loading,
      activeAccounts,
      allAccounts,
      getAccountEntry,
    }),
    [simplifiedStats, loading, activeAccounts, allAccounts, getAccountEntry],
  );

  return (
    <SimplifiedStatsContext.Provider value={value}>
      {children}
    </SimplifiedStatsContext.Provider>
  );
}

export function useSimplifiedStats() {
  const context = useContext(SimplifiedStatsContext);
  if (context === undefined) {
    throw new Error('useSimplifiedStats must be used within a SimplifiedStatsProvider');
  }
  return context;
}
